import { useEffect, useMemo, useState } from "react";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";
import { useGlobalOpsi } from "../components/GlobalOpsi";

export default function EditProfilUser({ requestSubmitGuard = async () => true }) {
  const { globalOpsi } = useGlobalOpsi();
  const opsiCabang = Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : [];
  const opsiJabatan = [
    { value: "Manager", label: "Manager" },
    { value: "Foreman", label: "Foreman" },
    { value: "Staff", label: "Staff" },
    { value: "Lainnya", label: "Lainnya" },
    { value: "Pembantu_Umum", label: "Pembantu Umum" },
    { value: "Staff_IT", label: "Staff IT" },
    { value: "Tireman", label: "Tireman" },
    { value: "Checker", label: "Checker" },
    { value: "Admin_Lokasi", label: "Admin Lokasi" },
  ];
  const jabatanByKey = new Map(opsiJabatan.map((item) => [
    String(item.value || "")
      .trim()
      .replace(/[\s-]+/g, "_")
      .replace(/_+/g, "_")
      .toLowerCase(),
    item.value,
  ]));
  const normalizeJabatanValue = (value) => {
    const key = String(value || "")
      .trim()
      .replace(/[\s-]+/g, "_")
      .replace(/_+/g, "_")
      .toLowerCase();
    return jabatanByKey.get(key) || String(value || "").trim();
  };
  const [opsiUserName, setOpsiUserName] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [nama, setNama] = useState("");
  const [kodeCabang, setKodeCabang] = useState("");
  const [jabatan, setJabatan] = useState("");
  const [userLevel, setUserLevel] = useState("");
  const [foto, setFoto] = useState(null);
  const [existingFoto, setExistingFoto] = useState("");
  const [loadingOpsi, setLoadingOpsi] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [alert, setAlert] = useState(null);
  const maxAssignableUserLevel = String(kodeCabang || "").trim().toUpperCase() === "C-1" ? 8 : 7;

  const fotoPreviewUrl = useMemo(() => {
    const hasNewFile = foto instanceof File;
    if (hasNewFile) return URL.createObjectURL(foto);
    return existingFoto ? `${backendPort}${existingFoto}` : "";
  }, [foto, existingFoto]);

  useEffect(() => {
    let mounted = true;
    const loadOpsiUserName = async () => {
      try {
        setLoadingOpsi(true);
        const { data } = await api.get(`${backendPort}/api/auth/load_opsi_nama_pegawai`);
        if (!mounted) return;
        const list = Array.isArray(data?.data) ? data.data : [];
        setOpsiUserName(list.map((x) => String(x?.username || "").trim()).filter(Boolean));
      } catch (err) {
        if (!mounted) return;
        setAlert({ type: "danger", message: err?.response?.data?.message || "Gagal memuat opsi username" });
      } finally {
        if (mounted) setLoadingOpsi(false);
      }
    };
    loadOpsiUserName();
    return () => { mounted = false; };
  }, []);

  useEffect(() => () => {
    if (fotoPreviewUrl && foto instanceof File) URL.revokeObjectURL(fotoPreviewUrl);
  }, [fotoPreviewUrl, foto]);

  const resetForm = () => {
    setNama("");
    setKodeCabang("");
    setJabatan("");
    setUserLevel("");
    setFoto(null);
    setExistingFoto("");
  };

  const loadDataUser = async (username) => {
    const clean = String(username || "").trim();
    if (!clean) {
      resetForm();
      return;
    }
    try {
      setLoadingData(true);
      setAlert(null);
      const { data } = await api.get(`${backendPort}/api/auth/load_data_user/${encodeURIComponent(clean)}`);
      const row = data?.data || {};
      setNama(String(row?.nama || ""));
      setKodeCabang(String(row?.kode_cabang || ""));
      setJabatan(normalizeJabatanValue(row?.jabatan));
      setUserLevel(String(Number.isFinite(Number(row?.user_level)) ? Number(row.user_level) : ""));
      setExistingFoto(String(row?.foto || "").trim());
      setFoto(null);
    } catch (err) {
      resetForm();
      setAlert({ type: "danger", message: err?.response?.data?.message || "Gagal memuat data user" });
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUsername || !nama.trim() || !kodeCabang || !jabatan || userLevel === "") {
      setAlert({ type: "danger", message: "Data belum lengkap" });
      return;
    }

    if (selectedUsername.trim().length < 3) {
      setAlert({ type: "danger", message: "Username tidak valid" });
      return;
    }

    if (nama.trim().length > 50) {
      setAlert({ type: "danger", message: "Nama pegawai maksimal 50 karakter" });
      return;
    }

    const userLevelNumber = Number(userLevel);
    if (!Number.isInteger(userLevelNumber) || userLevelNumber < 0 || userLevelNumber > 8) {
      setAlert({ type: "danger", message: "User level harus integer 0-8" });
      return;
    }
    if (String(kodeCabang || "").trim().toUpperCase() !== "C-1" && userLevelNumber > 7) {
      setAlert({ type: "danger", message: "User Cabang hanya boleh menggunakan user level 0-7" });
      return;
    }
    setLoadingSubmit(true);
    setAlert(null);
    try {
      const passed = await requestSubmitGuard("Guard Submit: Edit Profil User");
      if (!passed) {
        setLoadingSubmit(false);
        return;
      }
      const formData = new FormData();
      formData.append("nama", nama.trim());
      formData.append("kode_cabang", kodeCabang);
      formData.append("jabatan", jabatan);
      formData.append("user_level", String(userLevelNumber));
      if (foto) formData.append("foto", foto);
      const { data } = await api.put(
        `${backendPort}/api/auth/update_profil_user/${encodeURIComponent(selectedUsername)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setAlert({ type: "success", message: data?.message || "Profil user berhasil diupdate" });
      setSelectedUsername("");
      resetForm();
    } catch (err) {
      setAlert({ type: "danger", message: err?.response?.data?.message || "Gagal update profil user" });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div className="w-100">
      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="row g-2 align-items-end">
          <div className="col-2">
            <label className="form-label mb-1">Username</label>
            <select
              className="form-select form-select-sm"
              name="edit_profile_target_username"
              autoComplete="off"
              value={selectedUsername}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedUsername(val);
                loadDataUser(val);
              }}
              disabled={loadingOpsi || loadingData || loadingSubmit}
              required
            >
              <option value="">-Pilih Username-</option>
              {opsiUserName.map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="col-2">
            <label className="form-label mb-1">Nama Pegawai</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={nama}
              onChange={(e) => setNama(e.target.value)}
              maxLength={50}
              disabled={!selectedUsername || loadingData || loadingSubmit}
              required
            />
          </div>
          <div className="col-2">
            <label className="form-label mb-1">Cabang</label>
            <select
              className="form-select form-select-sm"
              value={kodeCabang}
              onChange={(e) => {
                const nextKodeCabang = e.target.value;
                setKodeCabang(nextKodeCabang);
                if (String(nextKodeCabang || "").trim().toUpperCase() !== "C-1" && Number(userLevel) > 7) {
                  setUserLevel("");
                }
              }}
              disabled={!selectedUsername || loadingData || loadingSubmit}
              required
            >
              <option value="">-Pilih Cabang-</option>
              {opsiCabang.map((x) => (
                <option key={x.kode_cabang} value={x.kode_cabang}>{x.nama_cabang}</option>
              ))}
            </select>
          </div>
          <div className="col-2">
            <label className="form-label mb-1">Posisi</label>
            <select
              className="form-select form-select-sm"
              value={jabatan}
              onChange={(e) => setJabatan(e.target.value)}
              disabled={!selectedUsername || loadingData || loadingSubmit}
              required
            >
              <option value="">-Pilih Posisi-</option>
              {opsiJabatan.map((x) => (
                <option key={x.value} value={x.value}>{x.label}</option>
              ))}
            </select>
          </div>
          <div className="col-2">
            <label className="form-label mb-1">User Level</label>
            <select
              className="form-select form-select-sm"
              value={userLevel}
              onChange={(e) => setUserLevel(e.target.value)}
              disabled={!selectedUsername || loadingData || loadingSubmit}
              required
            >
              <option value="">-Pilih Level-</option>
              {Array.from({ length: maxAssignableUserLevel + 1 }, (_, i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <div className="col-2 d-grid">
            <label className="form-label mb-1 text-white">.</label>
            <button
              type="submit"
              className="btn btn-primary btn-sm w-100"
              disabled={!selectedUsername || loadingData || loadingSubmit}
            >
              {loadingSubmit ? "Memproses..." : "Submit"}
            </button>
          </div>
        </div>
        <div className="row g-2 mt-2 align-items-end">
          <div className="col-4">
            <div className="mb-2">
              {fotoPreviewUrl ? (
                <img
                  src={fotoPreviewUrl}
                  alt="Preview foto user"
                  className="img-thumbnail"
                  style={{ width: 180, height: 180, objectFit: "cover" }}
                />
              ) : (
                <div className="small text-muted">Belum ada foto.</div>
              )}
            </div>
            <label className="form-label mb-1">Upload Foto</label>
            <input
              type="file"
              accept="image/*"
              className="form-control form-control-sm"
              onChange={(e) => setFoto(e.target.files?.[0] || null)}
              disabled={!selectedUsername || loadingData || loadingSubmit}
            />
          </div>
          <div className="col-8 text-end small text-muted">
            {loadingOpsi ? "Memuat opsi username..." : loadingData ? "Memuat data user..." : ""}
          </div>
        </div>
      </form>
      {alert && (
        <div className={`alert alert-${alert.type} mt-3`}>
          {alert.message}
        </div>
      )}
    </div>
  );
}
